/**
 * IssueActionControls Component
 *
 * Consolidated control section that includes:
 * - Segmented control (Original/Revised view toggle)
 * - Progress indicator
 * - Score delta indicator (tier-gated)
 * - Copy revised bar
 */

import React from 'react';
import { View } from 'react-native';
import { useTranslation } from '@dramadetector/shared';
import { trackEvent, type UserTier } from '@/lib/analytics/events';
import type { ViewMode, InlineIssue } from './types';
import { SegmentedControl } from './SegmentedControl';
import { ProgressIndicator } from './ProgressIndicator';
import { CopyRevisedBar } from './CopyRevisedBar';
import { ScoreDeltaIndicator } from './ScoreDeltaIndicator';
import { GatedSection } from '../../gating';
import { scoreDeltaStyles } from './inlineAnnotationsStyles';

export interface IssueActionControlsProps {
  /** Current view mode */
  viewMode: ViewMode;
  /** All issues */
  issues: InlineIssue[];
  /** Status counts (accepted, ignored, pending) */
  statusCounts: { accepted: number; ignored: number; pending: number };
  /** Revised text with accepted changes */
  revisedText: string;
  /** Whether there are any changes */
  hasChanges: boolean;
  /** Number of addressed issues */
  addressedCount: number;
  /** Score improvement delta (0 if tracking disabled) */
  scoreDelta: number;
  /** Original score (for analytics) */
  originalScore: number;
  /** Revised score (for analytics) */
  revisedScore: number;
  /** Whether tracking is enabled */
  isTrackingEnabled: boolean;
  /** Whether reduced motion is enabled */
  isReducedMotionEnabled: boolean;
  /** User's tier */
  tier: string;
  /** Screen name for analytics */
  screen: string;
  /** Callback when view mode changes */
  onViewModeChange: (mode: ViewMode) => void;
  /** Callback when upgrade CTA clicked (for tier-gated features) */
  onUpgrade?: () => void;
  /** Optional test ID */
  testID?: string;
}

/**
 * IssueActionControls - Consolidated control section
 */
export function IssueActionControls({
  viewMode,
  issues,
  statusCounts,
  revisedText,
  hasChanges,
  addressedCount,
  scoreDelta,
  originalScore,
  revisedScore,
  isTrackingEnabled,
  isReducedMotionEnabled,
  tier,
  screen,
  onViewModeChange,
  onUpgrade,
  testID = 'issue-action-controls',
}: IssueActionControlsProps) {
  const { t } = useTranslation('features');

  return (
    <>
      {/* Segmented Control */}
      {issues.length > 0 && (
        <SegmentedControl
          value={viewMode}
          onChange={onViewModeChange}
          hasChanges={hasChanges}
          testID={`${testID}-segment`}
        />
      )}

      {/* Progress Indicator */}
      {issues.length > 0 && (
        <ProgressIndicator
          addressedCount={addressedCount}
          totalCount={issues.length}
          testID={`${testID}-progress`}
        />
      )}

      {/* Score Delta Indicator - Tier-Gated (Drama+ required)
       * Phase 2: UX Gamification - "Score Chase" pattern
       * CTO Mandate: Must be wrapped in GatedSection for +15-25% conversion lift
       */}
      {scoreDelta > 0 && isTrackingEnabled && onUpgrade && (
        <GatedSection
          requiredTier="drama_plus"
          contentType="score_delta"
          screen={screen}
          onUpgrade={onUpgrade}
          behavior="blur"
          blurIntensity="light"
          teaser={t('gating.unlock_now')}
          testID={`${testID}-score-delta-gated`}
        >
          <View style={scoreDeltaStyles.container}>
            <ScoreDeltaIndicator
              scoreDelta={scoreDelta}
              isVisible={scoreDelta > 0}
              animationDelay={300}
              onAnimationComplete={() => {
                trackEvent('Score_Chase_Delta_Shown', {
                  delta_value: scoreDelta,
                  original_score: originalScore,
                  revised_score: revisedScore,
                  tier: tier as UserTier,
                  issues_accepted: statusCounts.accepted,
                  issues_total: issues.length,
                });
              }}
              testID={`${testID}-score-delta`}
            />
          </View>
        </GatedSection>
      )}

      {/* Copy Revised Bar */}
      <CopyRevisedBar
        revisedText={revisedText}
        acceptedCount={statusCounts.accepted}
        totalCount={issues.length}
        hasChanges={statusCounts.accepted > 0}
        testID={`${testID}-copy`}
      />
    </>
  );
}
